import React, { useState, useRef, useEffect } from 'react';
import './WhiteNoise.css';

const PRESETS = [
  { id: 'white', label: 'White Noise', icon: '⬜', desc: 'Classic broadband noise — masks all frequencies equally.' },
  { id: 'pink', label: 'Pink Noise', icon: '🌸', desc: 'Balanced low-emphasis noise — closest to natural sounds.' },
  { id: 'brown', label: 'Brown Noise', icon: '🟫', desc: 'Deep rumbling noise — similar to heavy rain or thunder.' },
  { id: 'rain', label: 'Rain', icon: '🌧', desc: 'Gentle rainfall — deeply relaxing and sleep-inducing.' },
  { id: 'ocean', label: 'Ocean Waves', icon: '🌊', desc: 'Rolling ocean waves — rhythmic and meditative.' },
  { id: 'fan', label: 'Fan / AC', icon: '🌀', desc: 'Steady fan hum — popular sleep and focus sound.' },
  { id: 'forest', label: 'Forest', icon: '🌲', desc: 'Birds and rustling leaves — natural focus environment.' },
  { id: 'fireplace', label: 'Fireplace', icon: '🔥', desc: 'Crackling fire — cozy, warm focus companion.' },
  { id: 'meditation', label: 'Meditation', icon: '🧘', desc: 'Tibetan bowl tones — deep meditation and mindfulness.' },
  { id: 'study', label: 'Study Room', icon: '📚', desc: 'Quiet library ambience with soft background activity.' },
  { id: 'programming', label: 'Programming', icon: '💻', desc: 'Low-key office sounds for coding sessions.' },
  { id: 'workout', label: 'Workout', icon: '💪', desc: 'Energetic ambient beat for exercise and movement.' },
];

function generateNoise(audioCtx, type, gainNode) {
  const bufferSize = audioCtx.sampleRate * 2;
  const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
  const data = buffer.getChannelData(0);

  if (type === 'white' || type === 'fan' || type === 'study' || type === 'programming') {
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
  } else if (type === 'pink') {
    let b0=0,b1=0,b2=0,b3=0,b4=0,b5=0,b6=0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99886*b0 + white*0.0555179; b1 = 0.99332*b1 + white*0.0750759;
      b2 = 0.96900*b2 + white*0.1538520; b3 = 0.86650*b3 + white*0.3104856;
      b4 = 0.55000*b4 + white*0.5329522; b5 = -0.7616*b5 - white*0.0168980;
      data[i] = (b0+b1+b2+b3+b4+b5+b6+white*0.5362) * 0.11;
      b6 = white * 0.115926;
    }
  } else if (type === 'brown' || type === 'rain' || type === 'ocean' || type === 'fireplace' || type === 'forest' || type === 'meditation' || type === 'workout') {
    let lastOut = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      data[i] = (lastOut + 0.02 * white) / 1.02;
      lastOut = data[i]; data[i] *= 3.5;
    }
  }

  const source = audioCtx.createBufferSource();
  source.buffer = buffer; source.loop = true;
  source.connect(gainNode);
  return source;
}

export default function WhiteNoise() {
  const [playing, setPlaying] = useState(false);
  const [activePreset, setActivePreset] = useState('white');
  const [volume, setVolume] = useState(60);
  const audioCtxRef = useRef(null);
  const gainRef = useRef(null);
  const sourceRef = useRef(null);

  const stopAll = () => {
    if (sourceRef.current) { try { sourceRef.current.stop(); } catch(e){} sourceRef.current = null; }
    if (audioCtxRef.current) { audioCtxRef.current.close(); audioCtxRef.current = null; }
    setPlaying(false);
  };

  const startPlay = (presetId, vol) => {
    stopAll();
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const gain = ctx.createGain();
    gain.gain.value = vol / 100;
    gain.connect(ctx.destination);
    const src = generateNoise(ctx, presetId, gain);
    src.start();
    audioCtxRef.current = ctx; gainRef.current = gain; sourceRef.current = src;
    setPlaying(true);
  };

  const handlePreset = (id) => {
    setActivePreset(id);
    if (playing) startPlay(id, volume);
  };

  const handleVolumeChange = (v) => {
    setVolume(v);
    if (gainRef.current) gainRef.current.gain.value = v / 100;
  };

  const togglePlay = () => {
    if (playing) stopAll();
    else startPlay(activePreset, volume);
  };

  useEffect(() => () => stopAll(), []);

  return (
    <main className="noise-page" style={{ paddingTop: '60px' }}>
      <section className="noise-hero">
        <div className="container">
          <div className="noise-badge">Free Audio Tool</div>
          <h1>White Noise & Ambient Sound Player</h1>
          <p>Block distractions, improve sleep, and boost focus with high-quality noise and ambient sounds. Plays entirely in your browser — no download required.</p>
        </div>
      </section>

      <div className="container noise-content">
        <div className="noise-player-wrap">
          <div className="presets-grid">
            {PRESETS.map(p => (
              <button key={p.id}
                className={`preset-btn ${activePreset === p.id ? 'active' : ''}`}
                onClick={() => handlePreset(p.id)}
              >
                <span className="preset-icon">{p.icon}</span>
                <span className="preset-label">{p.label}</span>
                {activePreset === p.id && playing && <span className="playing-dot" />}
              </button>
            ))}
          </div>

          <div className="player-controls">
            <div className="active-preset-info">
              <span className="aps-icon">{PRESETS.find(p => p.id === activePreset)?.icon}</span>
              <div>
                <div className="aps-name">{PRESETS.find(p => p.id === activePreset)?.label}</div>
                <div className="aps-desc">{PRESETS.find(p => p.id === activePreset)?.desc}</div>
              </div>
            </div>

            <div className="vol-row">
              <span className="vol-icon">🔈</span>
              <input type="range" min="0" max="100" value={volume}
                onChange={e => handleVolumeChange(Number(e.target.value))}
                className="vol-slider" />
              <span className="vol-icon">🔊</span>
              <span className="vol-val">{volume}%</span>
            </div>

            <button className={`play-btn ${playing ? 'playing' : ''}`} onClick={togglePlay}>
              {playing ? (
                <><span className="pause-icon">⏸</span> Pause</>
              ) : (
                <><span className="play-icon">▶</span> Start Playing</>
              )}
            </button>

            {playing && (
              <div className="now-playing">
                <div className="wave-bars">
                  {[...Array(12)].map((_,i) => <div key={i} className="wave-bar" style={{ animationDelay: `${i * 0.08}s` }} />)}
                </div>
                <span>Now Playing — {PRESETS.find(p => p.id === activePreset)?.label}</span>
              </div>
            )}
          </div>
        </div>

        <div className="noise-article">
          <h2>The Complete Guide to White Noise and Ambient Sound for Focus & Sleep</h2>
          <p>White noise and ambient sound tools have become one of the most practical productivity and wellness technologies available — helping millions of people fall asleep faster, concentrate more deeply, and manage the auditory distractions of modern environments. This guide explains the science behind noise-based focus and sleep aids, describes the differences between noise types, and provides practical guidance for integrating ambient sound into your daily routine.</p>

          <h3>What Is White Noise?</h3>
          <p>White noise is a type of random audio signal that contains equal intensity at all frequencies within the audible range (roughly 20Hz to 20,000Hz). The name derives from the analogy to white light, which contains all visible wavelengths in equal measure. Because white noise spreads energy uniformly across the frequency spectrum, it produces a constant, hissing sound that effectively masks other sounds by overwhelming the auditory system's ability to isolate and attend to specific signals in the background. When a sudden sound — a door slamming, traffic, a conversation — would normally trigger the startle response, white noise raises the auditory "floor" so that transient sounds don't rise above the threshold of conscious attention.</p>

          <h3>White Noise vs. Pink Noise vs. Brown Noise</h3>
          <p>While white noise is the most widely recognized, several noise "colors" exist and each has distinct acoustic and perceptual properties. White noise has equal energy per frequency — a bright, high-pitched hiss that some people find harsh for prolonged listening. Pink noise reduces energy proportionally as frequency increases, producing a balanced sound with more emphasis on lower frequencies. The result is a smoother, more natural-sounding noise that closely resembles rain on a window, waterfall spray, or wind through leaves. Research suggests pink noise may promote deeper, more restorative sleep than white noise for many individuals. Brown noise (also called red noise) reduces even more steeply with frequency, creating a deep, rumbling sound reminiscent of thunder, strong ocean waves, or roaring wind. Many people with ADHD report finding brown noise particularly effective for focus, possibly because the deep low-frequency content provides proprioceptive grounding without the cognitive stimulation of higher frequencies.</p>

          <h3>Scientific Evidence for Noise-Based Focus and Sleep</h3>
          <p>The cognitive science literature provides substantial support for white and pink noise as focus and sleep aids. A 2010 study in the journal Cognitive Processing found that moderately complex background noise — approximately 70 decibels, similar to the ambient sound in a coffee shop — enhanced creative task performance compared to both silence and high-noise environments, suggesting that a moderate level of non-structured auditory stimulation supports creative cognition through a mechanism called stochastic resonance. Multiple studies of NICU (neonatal intensive care unit) environments found that pink noise improved sleep duration and reduced cortisol levels in premature infants, providing some of the cleanest experimental evidence for noise-based sleep improvement because the confounding cognitive factors present in adult populations are absent.</p>
          <p>For adults, a 2017 study published in Frontiers in Human Neuroscience found that pink noise presented during sleep slow-wave activity (deep NREM sleep) significantly enhanced memory consolidation compared to a silent control condition, with participants showing markedly improved performance on declarative memory tests the following morning. The mechanism appears to involve synchronization of brain oscillations with the rhythmic noise signal, enhancing the slow-wave sleep periods most important for memory processing and physical restoration.</p>

          <h3>Best Noise Type for Different Activities</h3>
          <p>Different activities benefit from different noise profiles. For studying and focused work involving reading, writing, and analysis, pink noise at moderate volume (50–65% of maximum) provides background auditory structure without the cognitive demand of music with lyrics. For creative work and brainstorming, coffee shop-level ambient sound (our Study Room preset) at 60–70% volume provides the optimal level of background complexity identified in the Cognitive Processing study. For sleep and relaxation, brown noise and ocean wave sounds typically produce the deepest relaxation response in adults, with the slower spectral roll-off creating a more immersive low-frequency cocoon. For meditation, our Tibetan bowl preset provides rhythmic, sparse tonal anchors that support mindfulness practice without the continuous stimulation of white noise. For exercise, higher-energy presets with rhythmic structure support physical performance through entrainment — the body's tendency to synchronize movement with environmental rhythmic cues.</p>

          <h3>How to Use This Tool Effectively</h3>
          <p>For maximum benefit, start your noise session before you begin the task you want to support — beginning the audio 2–3 minutes before starting work allows your auditory system to habituate to the noise floor before cognitive demands begin. Set volume to a level where speech in the room becomes unintelligible at normal conversation distance but music lyrics from a phone on the desk would be audible — approximately 65–70 dB, which is the optimal masking level identified in most focus research. For sleep use, lower the volume to 50–55 dB (about the level of a gentle shower) and position any speaker or device at least 4–6 feet from your head to avoid potential hearing fatigue from extended near-ear exposure at higher volumes. Use the browser tab's ability to play audio while the screen is off or the tab is in the background — you don't need to keep this page visible for the audio to continue playing.</p>
        </div>
      </div>
    </main>
  );
}
